/**
 * Battle Cards — the thirty-two cards.
 *
 * A card is ONE COMBAT SPECIAL EFFECT wearing ONE GESTURE SUIT in ONE OF TWO PRINTINGS, named
 * for the effect it carries. Sixteen effects × Common and Rare; a pet equips four of them, one
 * per suit, and four copies of each is the sixteen-card pile that side draws from.
 *
 * ONE LIST NOW. {@link EFFECT_CARD_CATALOGUE} is every card that exists, and every one of them
 * is reachable — the testing trim that dealt four of sixteen is gone, replaced by
 * `DEFAULT_CARD_LOADOUT` in `cardLoadout.ts`, which is what a pet that has bought nothing plays.
 *
 * APPLY AND EXPLOIT ARE MERGED. There are no `exploit_*` cards. A card that only pays off
 * against a condition nobody guaranteed is a dead draw, so the CARD APPLIES the condition
 * and the player's MAGIC SIGNATURE is what cashes it in. That is also why three effects
 * are printed twice, in two suits: `dot`, `wound` and `shield` can be applied by winning
 * an attack OR by surviving a dodge, which the engine's own slot rules already permit and
 * already price differently.
 *
 * Every number here is DERIVED from real engine data wherever the game has any: every
 * effect is an `ActiveCombatSpecialEffect`, every duration and fraction comes from
 * `COMBAT_CONSTANTS`, and the base values come from `SUIKAMON_SPECIES`.
 *
 * Pure data and pure functions — no RNG, no I/O.
 *
 * Design: requirements/BATTLE_CARDS_V4_LOADOUTS.md
 */

import { BLEED_MAX_SHARE_PERCENT, COMBAT_CONSTANTS } from '../constants/battleConstants.js';
import {
  BASE_CARD_CHIP,
  CARD_TIER_BINARY_CHANCE,
  STUN_TIER_BINARY_CHANCE,
  CARD_TIER_ORDER,
  CARD_TIER_VALUE_SHARE,
  EFFECT_CARD_BACK_ART,
  EFFECT_CARD_FRAME_ART,
} from '../constants/cardConstants.js';
import { EVOLUTION_CARDS_PER_STEP } from './battleEvolution.js';
import { ALL_COMBAT_ACTIONS } from './combatActionRules.js';
import { COMBAT_ACTION_EMOJI, getCombatActionArt } from './combatSkillPresentation.js';
import { COMBAT_SPECIAL_EFFECT } from './petModels.js';
import type {
  ActiveCombatSpecialEffect,
  CombatSkill,
  CombatSpecialEffect,
  ElementId,
} from './petModels.js';
import { SUIKAMON_SPECIES } from './speciesConfig.js';
import { isGestureSlot, SUIKAMON_CARD_SLOT } from './cardTypes.js';
import type {
  CardTier,
  CardValueSource,
  EffectCard,
  EffectCardGesture,
  EffectCardId,
  EffectCardSlot,
} from './cardTypes.js';

/** Every suit, in the engine's canonical gesture order. */
export const EFFECT_DECK_GESTURES: readonly EffectCardGesture[] = ALL_COMBAT_ACTIONS;

/** Re-exported so a card face never has to import the constants module separately. */
export { EFFECT_CARD_FRAME_ART };

/**
 * `3` -> `3rd`. Needed because the streak rules are stated in ordinals but the limits they
 * come from live in `ACTION_LOCK_LIMITS` — hardcoding "third" would quietly lie the day a
 * limit moves.
 */
export function toOrdinal(value: number): string {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

/**
 * What each suit is FOR. Four roles to learn, not sixteen cards.
 *
 * Attack and dodge both leave a condition behind — that is the deck's spine, not a
 * duplication. The difference is what you pay: an attack has to WIN the exchange and lands
 * the bigger version, a dodge only has to SURVIVE it and lands the smaller.
 */
export const EFFECT_SUIT_ROLE: Record<
  EffectCardGesture,
  {
    label: string;
    emoji: string;
    role: string;
    rule: string;
    playerThinks: string;
    blurb: string;
  }
> = {
  attack: {
    label: 'Attack',
    emoji: COMBAT_ACTION_EMOJI.attack,
    role: 'Pressure',
    rule: 'Damage now, and a condition your signature can cash in later.',
    playerThinks: 'Hurt them and leave something behind.',
    blurb: 'Beats magic, loses to dodge.',
  },
  magic: {
    label: 'Magic',
    emoji: COMBAT_ACTION_EMOJI.magic,
    role: 'Control',
    rule: 'Takes the next turn away from your opponent.',
    playerThinks: 'Fire my signature and pick their gesture for them.',
    blurb: 'Beats dodge, loses to attack. The only suit that also fires your species effect.',
  },
  dodge: {
    label: 'Dodge',
    emoji: COMBAT_ACTION_EMOJI.dodge,
    role: 'Setup',
    rule: 'No damage — but you survive, and the condition still lands.',
    playerThinks: 'Live through it and put a condition on them anyway.',
    blurb: 'Beats attack, loses to magic.',
  },
  recover: {
    label: 'Recover',
    emoji: COMBAT_ACTION_EMOJI.recover,
    role: 'Sustain',
    rule: 'Buys back the HP the other three spent.',
    playerThinks: 'Heal and outlast.',
    blurb: 'Outside the triangle: beats every gesture, which is why you only get a handful.',
  },
};

// ============================================================================
// Base values, derived from the live species roster wherever the game has data
// ============================================================================

function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  return [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];
}

/** The skill a species runs in one slot, or undefined when it has no combat data. */
function skillFor(
  species: (typeof SUIKAMON_SPECIES)[number],
  gesture: EffectCardGesture
): CombatSkill | undefined {
  return species.combat?.[gesture];
}

/**
 * The base magnitude for an effect in a slot, read off the live roster.
 *
 * A card's suit IS the engine's slot, and the engine already prices each effect per slot —
 * a dodge `dot` is 4 where a magic `dot` is 22, because a dodge only fires on a successful
 * evade. Taking the median for the card's OWN slot is what keeps Bleed the dodge card
 * honest instead of borrowing magic's much larger number.
 *
 * Returns `undefined` when no species runs that effect in that slot, which is what routes
 * a card to its authored placeholder in {@link EFFECT_DECK}.
 */
export function getDerivedCardValue(
  effect: ActiveCombatSpecialEffect,
  gesture: EffectCardGesture
): number | undefined {
  const values = SUIKAMON_SPECIES.flatMap((species) => {
    const skill = skillFor(species, gesture);
    if (!skill || skill.special !== effect) return [];
    // Recover skills carry their magnitude as `heal`; every other slot uses specialValue.
    const magnitude = gesture === 'recover' ? skill.heal : skill.specialValue;
    return magnitude === undefined || magnitude <= 0 ? [] : [magnitude];
  });

  return median(values);
}

/**
 * The element a card's number is coloured by, read off the live roster.
 *
 * **THE SAME DERIVATION ROUTE AS THE VALUE**, one statistic along: `getDerivedCardValue` takes
 * the MEDIAN magnitude of the species running that effect in that slot, and this takes the
 * MODAL ELEMENT of exactly that population. A hand-typed element table would be a second set of
 * facts about the same nineteen species, and it would stop being true the first time one was
 * added; this re-derives.
 *
 * Ties break by the roster's own order, which is stable, so the catalogue is deterministic.
 * Returns `undefined` when no species runs that effect in that slot — those five ids fall back
 * to {@link CARD_ELEMENT_FALLBACK}.
 */
export function getDerivedCardElement(
  effect: ActiveCombatSpecialEffect,
  gesture: EffectCardGesture
): ElementId | undefined {
  const counts = new Map<ElementId, number>();

  for (const species of SUIKAMON_SPECIES) {
    const skill = skillFor(species, gesture);
    if (!skill || skill.special !== effect) continue;
    counts.set(species.element, (counts.get(species.element) ?? 0) + 1);
  }

  let modal: ElementId | undefined;
  let best = 0;
  for (const [element, count] of counts) {
    if (count > best) {
      best = count;
      modal = element;
    }
  }

  return modal;
}

/**
 * Elements for the five cards the roster cannot colour, because no species runs that effect in
 * that slot.
 *
 * Deliberately the same five ids as {@link AUTHORED_VALUES} and no others — a fallback list
 * longer than the set of cards that actually need one is a table pretending to be a derivation.
 * `cardCatalog.test.ts` pins the two key sets equal.
 */
/**
 * A colour chosen over the roster's modal one. The magic Execute is run by Toro, Talion and
 * Pikoto in three different colours, so the modal pick is a tie broken by roster order — and
 * landed on Thunder, leaving Fire (the Immolate kit's colour) with the thinnest matched-element
 * build (0.44, floor 0.5). Fire is where the card belongs and what the floor needs.
 */
const CARD_ELEMENT_OVERRIDE: Record<string, ElementId> = {
  'magic-execute': 'Fire',
};

const CARD_ELEMENT_FALLBACK: Record<string, ElementId> = {
  // A bleed that had to win the exchange: the roster's dodge and magic bleeds are Nature.
  'attack-dot': 'Nature',
  // Reading an attacker and hitting back is the Thunder kit's whole shape.
  'dodge-punish_attack': 'Thunder',
  // The drain takes the recover slot's own colour, which the roster's healers carry.
  'recover-drainlife': 'Water',
  // The FREEZE kit that stuns is Water and Thunder; Thunder, the stun element.
  'attack-strike_stunned': 'Thunder',
  // The WALL kit that holds shields is Water.
  'attack-strike_shielded': 'Water',
  /*
   * The finisher's heal: the EXECUTE kit's Nature (Pikoto).
   *
   * **THESE THREE COLOURS WERE CHOSEN BY SEARCH, AND THE CONSTRAINT IS THE OLD CARDS.** The
   * coverage pass below re-colours a card to give every colour two suits, so a new card's colour
   * can silently move an OLD one. The first cut (Water / Thunder / Nature) moved Execute
   * Thunder -> Fire and Drain Fire -> Water: Fire's fighters lost the only Recover card of their
   * colour, and the gauntlet's G10 (every card gets bought) fell to 0.86x with Drain unsold.
   * Chosen so every card that stayed keeps its colour, and each colour's matched-element build
   * reads what it did before the archetype cards (Nature 13, Water 14, Thunder 16, Fire 11 —
   * floor 0.69).
   */
  'recover-damage_vs_hurt': 'Nature',
  // The CRIPPLE kit's Water (Suimon) — the colour Lifesteal, which it replaced, carried, so
  // Water keeps a Recover card. On Nature the matched-element floor read 0.56 (Water 9).
  'recover-heal_vs_low': 'Water',
};

/**
 * The base heal a Recover card restores. **AUTHORED, and it used to be derived — read on,
 * because the reason it stopped is the whole point of the number.**
 *
 * `heal` as an EFFECT is what an attack does when it heals you on hit; the recover SLOT
 * carries its magnitude on the skill's own `heal` field with `special: none`, so it cannot
 * be found by matching on the effect and was read directly off the roster instead.
 *
 * **THE VALUE IS A DISCOUNTED RECOVER, and this is the one card that has to be.** Every other
 * card in the deck is derived from a `specialValue` — the BONUS a slot pays on top of the
 * skill's own work — but the recover slot has no bonus field, so its magnitude is the WHOLE
 * recover action. Printed undiscounted, the Heal card was the entire recover turn a second
 * time, added straight onto the turn it was riding, on the one suit that never has to win an
 * exchange to land. Two mistakes compounding: a measured 30 HP of card healing per recover
 * commit against 12 for attack and 7 for dodge, with three-quarters of all damage dealt in a
 * battle healed back off.
 *
 * `EFFECT_WHEEL_FIRE_ODDS` (`cardConstants.ts`) was the honest price, because it is exactly
 * what the recover
 * suit is not paying: the other three fire about half the time and are printed at full face,
 * so a card that fires every time is printed at half. **11 is that rule's own output** — half
 * the roster median of 22 that the ladder carried when the card was priced — kept as the
 * number rather than the formula.
 *
 * **WHY IT IS NO LONGER READ OFF THE ROSTER (tuning log Row 53).** Reading the live median made
 * the card a passenger on the recover LADDER, and the two are tuned for different questions:
 * the ladder is how big the ❤️ BUTTON is, the card is how big a RIDER on it is. Raising every
 * species' Recover by half to answer the first question silently answered the second as well —
 * the median went 22 → 33 and the card went 11 → 17 with nobody deciding it, dragging
 * `recover-lifesteal` (7 → 11) and `recover-drainlife` (14 → 21) along behind it, because both
 * are quoted as fractions of this one. A skill buff became a three-card buff in the same commit.
 *
 * **What that costs, stated plainly:** the card no longer tracks the roster, so if the recover
 * ladder is re-tuned again this number does NOT follow and somebody has to decide it on
 * purpose. That is the trade — an explicit decision every time, instead of an automatic one
 * nobody sees. Move it deliberately, with a gauntlet run, or not at all.
 */
export const HEAL_CARD_VALUE = 11;

/**
 * The Heal card's printed value. Kept as a function, and kept exported under this name,
 * because it is the single read every recover card's number goes through — the other two are
 * quoted as fractions of it — and a constant inlined at each of those sites is how they start
 * disagreeing.
 */
export function getDerivedRecoverHeal(): number | undefined {
  return HEAL_CARD_VALUE;
}

/**
 * The Heal card's element, read off the same population its value is.
 *
 * Needed for the same reason {@link getDerivedRecoverHeal} is: the recover slot carries its
 * magnitude on `heal` with `special: none`, so matching on the effect finds nobody and the
 * generic {@link getDerivedCardElement} would hand this card to the fallback table. Deriving it
 * from the healers themselves keeps the one card the roster CAN colour out of that table.
 */
export function getDerivedRecoverElement(): ElementId | undefined {
  const counts = new Map<ElementId, number>();

  for (const species of SUIKAMON_SPECIES) {
    const heal = species.combat?.recover.heal;
    if (heal === undefined || heal <= 0) continue;
    counts.set(species.element, (counts.get(species.element) ?? 0) + 1);
  }

  let modal: ElementId | undefined;
  let best = 0;
  for (const [element, count] of counts) {
    if (count > best) {
      best = count;
      modal = element;
    }
  }

  return modal;
}

/**
 * How the other recover cards sit either side of Heal.
 *
 * Both were authored as bare numbers positioned "below Heal" and "above Heal" — a
 * relationship the numbers only happened to hold, and stopped holding the moment Heal was
 * re-priced. Stated as fractions OF Heal instead, so the ordering the comments claim is the
 * ordering the deck actually prints, whatever the roster does to the median next.
 */
/**
 * **The share the two CONDITIONAL heals print at — inherited from Lifesteal, which they replaced.**
 *
 * History, kept because the number came from it — **0.65 -> 0.80, Row 66. The discount was
 * charged for a condition the card does not pay.**
 *
 * Lifesteal is priced below Heal because "it only pays when you connect" — and the per-card
 * table says it connects as often as Heal does. All three recover cards fire in ~89% of the
 * commits they appear in (Heal 89%, Lifesteal 89%, Drain 90%), so the conditionality the
 * discount is charged for never shows up as a lower fire rate.
 *
 * What it did show up as is a card nobody reaches for: in its own slot Lifesteal measured
 * **48.7 / 46.0 / 49.7%** against a 45-55% band and was the outright best pick in **zero** of
 * the 76 (species x slot) contexts at Rare and Common, while its two slot-mates read 54.8% and
 * 53.5% and took 6 and 10 contexts. A slot is a slot: 38.5 HP per fire against Heal's 57.1 is
 * not a trade, it is a worse card.
 *
 * 0.80 keeps the ORDERING the comments claim — Lifesteal below Heal below Drain, 9 / 11 / 14 —
 * while closing a gap that was buying the player nothing.
 */
const CONDITIONAL_HEAL_SHARE_OF_HEAL = 0.8;
const DRAIN_SHARE_OF_HEAL = 1.25;

/** Heal's own printed value — the anchor the other two recover cards are quoted against. */
function healCardValue(): number {
  return getDerivedRecoverHeal() ?? 0;
}

/**
 * Authored base values — the cards the live roster cannot price, because no species runs
 * that effect in that slot. Placeholders pending a balance pass, positioned relative to
 * the numbers that ARE real rather than picked from nowhere.
 */
const AUTHORED_VALUES: Record<string, number> = {
  // Between the dodge Bleed (4) and the magic Bleed (22): an attack has to win the
  // exchange, but it does not cost you your signature turn. Round numbers on purpose —
  // the whole ladder is read off this one (10 / 20 / 30 / 40), which is what makes the
  // TICKS the card prints round too (3 / 5 / 8 / 10). A bleed a player has to do
  // arithmetic on is a bleed they stop counting, which is why the card face shows the
  // tick and never this value — see `getCardPreview`.
  'attack-dot': 10,
  /*
   * In line with the exploit bonuses the roster runs on attack (13-17).
   *
   * **A CUT TO 11 WAS TRIED AND REVERTED — Row 66, and the reason is worth keeping.** This card
   * sits well above the competitive band at every printing (58.1 / 58.4 / 59.1% against 45-55%,
   * measured at ±1.5) and is the outright best pick in 17 of the 76 (species x slot) contexts,
   * second only to Execute. Dropping the face by a fifth moved NONE of that: the win rates
   * stayed put inside the margin and the contexts went UP.
   *
   * **Its power is the CONDITION, not the number.** It pays 46.7 HP per fire because it reads a
   * GESTURE — the only card in the deck that does — and a fifth off a payout that already only
   * fires 16% of the time is lost in the noise. The lever, if this card is to come into band, is
   * what fraction of the attack it punishes, or how often the condition is readable. Not this
   * value. Cutting further would gut the card's identity before it reached the band.
   */
  'dodge-punish_attack': 14,
  // Above Heal: it does not land until your next turn.
  'recover-drainlife': Math.round(healCardValue() * DRAIN_SHARE_OF_HEAL),
  /*
   * **THE ARCHETYPE CARDS — one condition the player can see, one number.** Each is priced
   * against the card in its own suit that reads a condition the same way, so the comparison a
   * player makes on the shelf is the one the numbers were set by.
   *
   * Punish Stun reads "the foe is stunned" — rarer than Punish Attack's "their last move was
   * Attack", which a stun alone cannot set — so it prints at Punish Attack's face.
   */
  'attack-strike_stunned': 14,
  // Shield Strike reads your OWN shield, which a WALL holds most turns: priced at the attack
  // Shield it replaced (8), the card that used to put that armour on an attack.
  'attack-strike_shielded': 8,
  // Both conditional cards start at the discount Lifesteal printed and DOUBLE when the condition is up —
  // so with the condition they out-pay Drain, and without it they are the slot's smallest. Reaper
  // is priced off Heal's number but paid as DAMAGE: the recover turn's HP, spent on the foe.
  'recover-damage_vs_hurt': Math.round(healCardValue() * CONDITIONAL_HEAL_SHARE_OF_HEAL),
  'recover-heal_vs_low': Math.round(healCardValue() * CONDITIONAL_HEAL_SHARE_OF_HEAL),
};

/**
 * Effects with no magnitude at all — you are stunned or you are not, wounded or not,
 * locked out or not. These carry {@link EFFECT_BINARY_BASE_CHANCE} instead of a value.
 */
export const BINARY_CARD_EFFECTS: readonly ActiveCombatSpecialEffect[] = [
  COMBAT_SPECIAL_EFFECT.Stun,
  COMBAT_SPECIAL_EFFECT.Wound,
  COMBAT_SPECIAL_EFFECT.ForceRecover,
  COMBAT_SPECIAL_EFFECT.BlockMagic,
  COMBAT_SPECIAL_EFFECT.BlockRecover,
  COMBAT_SPECIAL_EFFECT.ForceAttack,
];

/**
 * The effects whose magnitude is HEALING — the cards {@link RECOVER_MELD_HEAL_BONUS} pays on.
 *
 * **Stated as effects rather than as the recover SUIT, and the difference is not pedantry.**
 * The recover suit holds two cards this rider must not reach: the binary Force Recover, which
 * has no magnitude at all, and the base recover card, whose {@link BASE_CARD_CHIP} the empty
 * slot pays as HP. That second one heals, so a bonus keyed on the SUIT would multiply it — and
 * a meld bonus is what a collected skill buys. The blank is a floor and stays flat, for the same
 * reason it takes no matched-element bonus: a slot you have collected nothing for is not where
 * the deck pays out for building one.
 *
 * All three sit on recover today. Listing them by effect is what keeps the rider correct if one
 * is ever printed on another suit, the way `dot`, `wound` and `shield` already are.
 */
export const HEALING_CARD_EFFECTS: readonly ActiveCombatSpecialEffect[] = [
  COMBAT_SPECIAL_EFFECT.Heal,
  COMBAT_SPECIAL_EFFECT.Lifesteal,
  COMBAT_SPECIAL_EFFECT.DrainLife,
  COMBAT_SPECIAL_EFFECT.HealVsLow,
];

/** Does this card's number count HP restored to its holder? */
export function isHealingCard(card: Pick<EffectCard, 'effect'>): boolean {
  return HEALING_CARD_EFFECTS.includes(card.effect as ActiveCombatSpecialEffect);
}

const WOUND_PERCENT = Math.round(COMBAT_CONSTANTS.WOUNDED_HEAL_MULTIPLIER * 100);
const LOW_HP_HEAL_PERCENT = Math.round(COMBAT_CONSTANTS.LOW_HP_HEAL_THRESHOLD * 100);
const EXECUTE_PERCENT = Math.round(COMBAT_CONSTANTS.EXECUTE_THRESHOLD * 100);

/** Everything about a card except its number, which is resolved below. */
interface CardDefinition {
  effect: ActiveCombatSpecialEffect;
  gesture: EffectCardGesture;
  name: string;
  headline: string;
  description: string;
  unit?: string;
}

/**
 * The sixteen cards.
 *
 * FOUR PER SUIT, which is what lets every suit be playable — a meld is played with a
 * committed gesture — and keeps the shelf simple. Five of them are the archetype cards (Punish
 * Stun, Shield Strike, Force Attack, Execute Heal, Wound Heal), which took the slots of five
 * retired cards ({@link RETIRED_CARD_SUCCESSORS}); each reads a single condition already on the
 * board — the rule for any card added after them.
 *
 * `dot` is the one effect printed TWICE, on attack and dodge. `attack-dot` and `dodge-dot` are
 * separate ids, so holding one of each is NOT a pair.
 *
 * Every card has its OWN drawing, `/cards/skills/<gesture>-<effect>.png` — see {@link skillArtPath}.
 */
const CARD_DEFINITIONS: readonly CardDefinition[] = [
  // ⚔️ Attack — pressure. Damage now, plus something your signature can cash in.
  {
    effect: COMBAT_SPECIAL_EFFECT.Execute,
    gesture: 'attack',
    name: 'Execute',
    headline: 'Ends a hurt opponent.',
    description: `Bonus damage when the target is below ${EXECUTE_PERCENT}% of their max HP. Nothing at all above it.`,
    unit: 'dmg',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.Dot,
    gesture: 'attack',
    name: 'Bleed',
    headline: 'Hits, and DEEPENS a bleed.',
    description: `The number is what it takes off at the end of each of the next ${COMBAT_CONSTANTS.BLEED_DURATION_TURNS} turns. More copies is a bigger tick, never a longer bleed. **The only card that stacks on a bleed already running** — every other source just takes the bigger tick, this one adds to it. Land a bleed, then compound it — up to the ceiling no bleed may pass, ${BLEED_MAX_SHARE_PERCENT}% of the target's max HP.`,
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.StrikeStunned,
    gesture: 'attack',
    name: 'Punish Stun',
    headline: 'Hits a stunned foe harder.',
    description: 'Bonus damage if the foe is stunned.',
    unit: 'dmg',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.StrikeShielded,
    gesture: 'attack',
    name: 'Shield Strike',
    headline: 'Hits harder behind a shield.',
    description: 'Bonus damage if you have a shield.',
    unit: 'dmg',
  },

  // ✨ Magic — control. Every card spends your turn to spend theirs.
  {
    effect: COMBAT_SPECIAL_EFFECT.Stun,
    gesture: 'magic',
    name: 'Stun',
    headline: 'Takes their turn.',
    description:
      'The target loses their next turn. They are stun-immune the turn after, so it never chains.',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.Execute,
    gesture: 'magic',
    name: 'Execute',
    headline: 'Ends a hurt opponent, by magic.',
    description: `Bonus damage when the target is below ${EXECUTE_PERCENT}% of their max HP. Nothing at all above it. The same card as the attack Execute, for the turns you cast instead of strike.`,
    unit: 'dmg',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.BlockRecover,
    gesture: 'magic',
    name: 'Block Recover',
    headline: 'Shuts off healing.',
    description: 'The target cannot commit recover next turn. The setup for an Execute.',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.ForceAttack,
    gesture: 'magic',
    name: 'Force Attack',
    headline: 'Makes them attack.',
    description: 'The foe must Attack next turn.',
  },

  // 🛡️ Dodge — setup. You take no damage, but the condition lands anyway.
  {
    effect: COMBAT_SPECIAL_EFFECT.Dot,
    gesture: 'dodge',
    name: 'Bleed',
    headline: 'Evades, and starts a bleed.',
    description: `The number is what it takes off at the end of each of the next ${COMBAT_CONSTANTS.BLEED_DURATION_TURNS} turns. More copies is a bigger tick, never a longer bleed. Smaller than the attack Bleed — you only had to survive.`,
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.Wound,
    gesture: 'dodge',
    name: 'Wound',
    headline: 'Evades, and halves their healing.',
    description: `Every heal the target takes is halved (${WOUND_PERCENT}%) for ${COMBAT_CONSTANTS.WOUND_DURATION_TURNS} turns. It expires on its own — the only thing that lifts it early is a full Recover commit.`,
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.Shield,
    gesture: 'dodge',
    name: 'Shield',
    headline: 'Evades, and armours up.',
    description:
      'Raises a shield that soaks incoming damage and bleed ticks until it is used up. Copies played together are ONE bigger shield; a shield raised on a later round replaces what is left of this one rather than adding to it.',
    unit: 'shield',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.PunishAttack,
    gesture: 'dodge',
    name: 'Punish Attack',
    headline: 'Punishes an attacker.',
    description:
      "Bonus damage if the target's last commit was attack. The one card that reads a gesture rather than a condition.",
    unit: 'dmg',
  },

  // 🌿 Recover — sustain. The HP the other three suits are spending.
  {
    effect: COMBAT_SPECIAL_EFFECT.Heal,
    gesture: 'recover',
    name: 'Heal',
    headline: 'Heals you now.',
    description: `Restores HP immediately. Halved to ${WOUND_PERCENT}% while you are wounded.`,
    unit: 'HP',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.DrainLife,
    gesture: 'recover',
    name: 'Drain',
    headline: 'Heals you next turn.',
    description: `Queues a heal that lands at the start of your next turn. Slower than Heal, larger. Halved to ${WOUND_PERCENT}% if you are still wounded when it lands.`,
    unit: 'HP',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.DamageVsHurt,
    gesture: 'recover',
    name: 'Reaper',
    headline: 'Cuts down a hurt opponent.',
    description: `Hits the foe, even on a turn you spent recovering. Double if the foe is below ${EXECUTE_PERCENT}% HP.`,
    unit: 'dmg',
  },
  {
    effect: COMBAT_SPECIAL_EFFECT.HealVsLow,
    gesture: 'recover',
    name: 'Last Stand',
    headline: 'Heals more when you are low.',
    description: `Heals you. Double while you are below ${LOW_HP_HEAL_PERCENT}% HP.`,
    unit: 'HP',
  },
];

/** The Rare printing's number, or undefined when the effect is binary. */
function resolveCardValue(definition: CardDefinition): number | undefined {
  if (BINARY_CARD_EFFECTS.includes(definition.effect)) return undefined;

  return definition.gesture === 'recover' && definition.effect === COMBAT_SPECIAL_EFFECT.Heal
    ? getDerivedRecoverHeal()
    : getDerivedCardValue(definition.effect, definition.gesture);
}

/**
 * A card's id, for a gesture, an effect and a printing.
 *
 * **Rares are unsuffixed**, which is the whole scheme: the sixteen ids that already sit inside
 * live `pvp_battles.battle_state` blobs keep meaning exactly what they meant, so nothing
 * persisted needs migrating. The single resolver that reads one back is
 * {@link getEffectCardById}.
 */
export function cardIdFor(
  gesture: EffectCardGesture,
  /** Any effect, `none` included — the four base cards are `{gesture}-none`. */
  effect: CombatSpecialEffect,
  tier: CardTier
): EffectCardId {
  // RARE KEEPS THE UNSUFFIXED ID IT ALWAYS HAD — stored `battle_state` blobs name it, so it
  // must never move. Every other printing suffixes its own tier.
  return tier === 'rare' ? `${gesture}-${effect}` : `${gesture}-${effect}-${tier}`;
}

/**
 * The element a card carries, or undefined for a binary card, which never carries one.
 *
 * Binary cards are excluded HERE rather than at the call site because it is the same rule the
 * value follows: matched element multiplies a magnitude, and a card with no magnitude has
 * nothing for it to multiply. Boosting the chance instead would break the invariant that four
 * Rare copies is exactly certainty — see `applyMatchedElement`.
 */
function modalCardElement(definition: CardDefinition): ElementId | undefined {
  if (BINARY_CARD_EFFECTS.includes(definition.effect)) return undefined;

  const derived =
    definition.gesture === 'recover' && definition.effect === COMBAT_SPECIAL_EFFECT.Heal
      ? getDerivedRecoverElement()
      : getDerivedCardElement(definition.effect, definition.gesture);

  return derived ?? CARD_ELEMENT_FALLBACK[cardIdFor(definition.gesture, definition.effect, 'rare')];
}

/**
 * Fire's share of the species running each effect in each slot — the runner-up table the
 * coverage pass below picks from.
 */
function elementShareOfCard(definition: CardDefinition, element: ElementId): number {
  let matching = 0;
  let total = 0;
  for (const species of SUIKAMON_SPECIES) {
    const skill = skillFor(species, definition.gesture);
    if (!skill || skill.special !== definition.effect) continue;
    total += 1;
    if (species.element === element) matching += 1;
  }
  return total === 0 ? 0 : matching / total;
}

/**
 * **EVERY ELEMENT THE ROSTER HAS MUST APPEAR ON A CARD.**
 *
 * The modal rule alone is a broken heuristic, and the measurement is unambiguous: it gave Water
 * four cards, Nature three, Thunder two and **Fire none at all** — so the three Fire species could
 * never receive matched-element (STAB) under any loadout. A mechanic sixteen species have and
 * three cannot is not a balance number, it is a fairness bug, and it showed up the moment the card
 * AI was taught to value STAB: grouped by the species' own element, that change was worth
 * **Water +2.2, Nature −0.7, Thunder −0.9, Fire −2.0 points** — ordered almost exactly by how many
 * cards carry each colour.
 *
 * Fire loses because it is never the PLURALITY: only three of nineteen species are Fire, so a
 * modal vote can never land on it even where Fire is heavily represented. Bleed on the dodge slot
 * is **40% Fire** (two of five species) and was still coloured Nature on a 3-2 count.
 *
 * So after the modal pass, any element short of the floor takes the card where it holds the
 * largest share — its best near-miss. Stated as a rule rather than a hand-picked recolour,
 * because it has to keep holding as species are added: a new Fire species should be able to
 * shift which card carries Fire, and a fifth element should get a card without anyone
 * remembering to give it one.
 *
 * **THE FLOOR IS SLOTS, NOT CARDS — and it used to be one card, which was the floor of the fix
 * rather than the fix.** One card is not a build. It is a single card that happens to be your
 * colour, competing for its slot against three cards that do more, and whether you play "Fire"
 * is decided by whether it loses that one comparison. Measured on the deck the one-card rule
 * produced, a Fire Keeper could equip **3 points of matched bonus across 1 of the 4 slots**
 * while a Water Keeper had **17 across 3** — and the pooled win rates ran in exactly that order
 * (Fire 48.2%, Nature 49.9%, Thunder 50.1%, Water 50.9%). That is criterion 24, and the deck
 * failed it at 1 slot against a floor of 2 and 0.18x against a floor of 0.50x.
 *
 * {@link MIN_ELEMENT_COLOUR_SLOTS} is the floor the rule now fills to, and it is DELIBERATELY
 * not imported from `gauntletCriteria.ts`: the criterion has to be able to fail. A rule that
 * read its own band would be measuring itself, and the day the deck runs out of cards to
 * re-colour with — every donor already at its own floor — the criterion is the thing that says
 * so. Exactly the arrangement criterion 5's reachability check uses.
 *
 * Two guard rails, both about the colour on the OTHER side of a re-colouring:
 *
 * - **A donor never falls below the floor.** Feeding one colour by starving another is not a
 *   fix, it is the same bug pointed somewhere else.
 * - **A card the roster can colour is preferred to one it cannot.** The near-miss share is the
 *   existing principle and it stays first; the fallback cards (the five no species runs, see
 *   {@link CARD_ELEMENT_FALLBACK}) are only reached when no near-miss is available, and among
 *   those the rule takes from the colour that can most afford it, largest card first — a colour
 *   with one slot needs a card worth building around, not the smallest number on the shelf.
 *
 * Only elements the ROSTER actually fields are covered — `ComingSoon` is not an element anybody
 * plays, and reserving a card for it would take one from a colour that has players.
 */
/**
 * **HOW MANY SLOTS A COLOUR MUST BE BUILDABLE IN.**
 *
 * Two of the four, because a loadout is four slots and two is the smallest number at which "I
 * build Fire" is a BUILD rather than one card that happens to be Fire. The magic suit is
 * entirely binary and carries no colour at all, so three is the most any colour could reach and
 * this asks for two of those three.
 *
 * **Not imported from `gauntletCriteria.ts`, on purpose.** Criterion 24 scores the deck against
 * the same number, and a rule that read the band it is scored against would be marking its own
 * homework — the criterion could then never fail, including on the day the deck genuinely runs
 * out of cards to re-colour with. The two numbers are set together in the tuning log and kept
 * apart in the code.
 */
const MIN_ELEMENT_COLOUR_SLOTS = 2;

function assignCardElements(): Map<string, ElementId | undefined> {
  const assigned = new Map<string, ElementId | undefined>();
  for (const definition of CARD_DEFINITIONS) {
    assigned.set(cardKey(definition), CARD_ELEMENT_OVERRIDE[cardKey(definition)] ?? modalCardElement(definition));
  }

  const rosterElements = [
    ...new Set(
      SUIKAMON_SPECIES.filter((species) => species.combat !== undefined).map(
        (species) => species.element
      )
    ),
  ];

  /** The loadout slots a colour can be built in, as the assignment currently stands. */
  const slotsHeld = (element: ElementId): Set<EffectCardGesture> => {
    const slots = new Set<EffectCardGesture>();
    for (const definition of CARD_DEFINITIONS) {
      if (assigned.get(cardKey(definition)) === element) slots.add(definition.gesture);
    }
    return slots;
  };

  /**
   * One re-colouring, or nothing. Returns the card key to hand to `element`, preferring its
   * best near-miss and falling back to the richest donor's biggest card — never taking from a
   * colour that is itself at the floor.
   */
  const findDonor = (element: ElementId): string | undefined => {
    const held = slotsHeld(element);
    let nearMissKey: string | undefined;
    let nearMissShare = 0;
    let fallbackKey: string | undefined;
    let fallbackRank = { slots: 0, value: 0 };

    for (const definition of CARD_DEFINITIONS) {
      const key = cardKey(definition);
      const owner = assigned.get(key);
      // A binary card carries no colour at all, and a slot this element already holds buys it
      // nothing: the floor is SLOTS.
      if (owner === undefined || owner === element || held.has(definition.gesture)) continue;
      // Never starve the colour being taken from. A donor may drop to the floor, never below.
      if (slotsHeld(owner).size <= MIN_ELEMENT_COLOUR_SLOTS) continue;

      const share = elementShareOfCard(definition, element);
      if (share > nearMissShare) {
        nearMissShare = share;
        nearMissKey = key;
        continue;
      }
      if (share > 0) continue;

      // No share to go on: take from whichever colour can most afford it, biggest card first.
      const rank = {
        slots: slotsHeld(owner).size,
        value: resolveCardValue(definition) ?? AUTHORED_VALUES[key] ?? 0,
      };
      if (
        rank.slots > fallbackRank.slots ||
        (rank.slots === fallbackRank.slots && rank.value > fallbackRank.value)
      ) {
        fallbackRank = rank;
        fallbackKey = key;
      }
    }

    return nearMissKey ?? fallbackKey;
  };

  /*
   * Neediest colour first, and re-derived on every pass rather than sorted once: a re-colouring
   * moves a slot from one colour to another, so the ordering this loop was started with is stale
   * the moment it makes its first move. It stops when no colour short of the floor can be fed —
   * either because every colour is there, or because every donor is at the floor too, and the
   * second case is a real deck constraint that criterion 24 is the thing that reports.
   */
  for (let pass = 0; pass < CARD_DEFINITIONS.length; pass += 1) {
    const needy = rosterElements
      .filter((element) => slotsHeld(element).size < MIN_ELEMENT_COLOUR_SLOTS)
      .sort((a, b) => slotsHeld(a).size - slotsHeld(b).size);
    if (needy.length === 0) break;

    const taken = needy.map((element) => ({ element, key: findDonor(element) }));
    const move = taken.find((entry) => entry.key !== undefined);
    if (move === undefined) break;

    assigned.set(move.key!, move.element);
  }

  return assigned;
}

/** Stable key for a definition — the Rare id, which is the unsuffixed one. */
function cardKey(definition: CardDefinition): string {
  return cardIdFor(definition.gesture, definition.effect, 'rare');
}

/** Every card's element, assigned once with the coverage pass applied. */
const CARD_ELEMENTS = assignCardElements();

function resolveCardElement(definition: CardDefinition): ElementId | undefined {
  return CARD_ELEMENTS.get(cardKey(definition));
}

/** A card's drawing: one file per skill identity, shared by its Common and Rare printings. */
function skillArtPath(definition: CardDefinition): string {
  return `/cards/skills/${definition.gesture}-${definition.effect}.png`;
}

/**
 * One printing of one definition: binary, derived from the roster, or authored — then scaled
 * to the tier.
 *
 * **THE COMMON'S NUMBER IS NEVER AUTHORED.** It is its Rare twin's, times
 * {@link COMMON_TIER_VALUE_SHARE}, so re-pricing a card moves both printings together and the
 * two can never drift into a Common that beats its own Rare.
 */
function buildCard(definition: CardDefinition, tier: CardTier): EffectCard {
  const rareId = cardIdFor(definition.gesture, definition.effect, 'rare');
  const id = cardIdFor(definition.gesture, definition.effect, tier);

  if (BINARY_CARD_EFFECTS.includes(definition.effect)) {
    // Table lookup, not a branch — a binary card's rate per tier is stated once in the
    // constants (and deliberately stops climbing at Rare, where a full stack is certainty).
    const chance = definition.effect === COMBAT_SPECIAL_EFFECT.Stun
      ? STUN_TIER_BINARY_CHANCE[tier]
      : CARD_TIER_BINARY_CHANCE[tier];
    return { ...definition, art: skillArtPath(definition), id, tier, chance };
  }

  const rareValue = resolveCardValue(definition) ?? AUTHORED_VALUES[rareId];
  if (rareValue === undefined) {
    throw new Error(`Card ${rareId} has no derived value and no authored fallback`);
  }

  // Every printing is the base (Rare) number times its tier's share — Common down, Epic up
  // — so re-pricing a card moves all three together and they can never cross.
  //
  // **THE LADDER IS EVEN: the Epic is as far above the Rare as the Common is below it.** Rounding
  // each rung on its own made the steps lopsided on a small number (Bleed 10 read 9 / 10 / 12:
  // −1 down, +2 up), so the top rung was worth twice the one under it for no reason anyone chose.
  const commonValue = Math.round(rareValue * CARD_TIER_VALUE_SHARE.common);
  const value =
    tier === 'common' ? commonValue : tier === 'epic' ? rareValue + (rareValue - commonValue) : rareValue;
  return { ...definition, art: skillArtPath(definition), id, tier, value, element: resolveCardElement(definition) };
}

/**
 * Every card that exists, numbers resolved — sixteen effects in two printings.
 *
 * **ORDER IS ALL SIXTEEN RARES, THEN ALL SIXTEEN COMMONS**, and it is load-bearing:
 * `getMeldStrength`, `getCommittedMeld` and `findBestMeld` all break ties by catalogue order,
 * so a tie prefers the Rare — consistent with the tie rule, which does the same thing where
 * a tier decides an outcome rather than a size.
 */
export const EFFECT_CARD_CATALOGUE: readonly EffectCard[] = [...CARD_TIER_ORDER]
  .reverse()
  .flatMap((tier) => CARD_DEFINITIONS.map((definition) => buildCard(definition, tier)));

/** Every card's id, catalogue order. */
export const EFFECT_CARD_CATALOGUE_IDS: readonly EffectCardId[] = EFFECT_CARD_CATALOGUE.map(
  (card) => card.id
);

/**
 * Renamed ids, pointing at what they are now.
 *
 * **EMPTY ON DAY ONE, AND THAT IS THE POINT.** Ids persist into `battle_state` JSONB, so the
 * day a card is renamed there will be stored battles naming the old id — and the resolver
 * below is exactly where nobody wants to be adding a branch under pressure. The home for it
 * exists before it is needed.
 */
/**
 * **THE FIVE CARDS RETIRED FOR THE ARCHETYPE CARDS — and where each one's copies went.**
 *
 * Four skills per suit is the rule ("keep it simple"), so the five archetype cards each took a
 * slot. Every retired card points at its successor: the same card on its other suit where one
 * exists (Wound, Shield), otherwise the new card in the same slot. Copies a pet owned of a
 * retired card COUNT toward the successor ({@link currentCardId}), so nothing collected is lost
 * and no database row has to be rewritten.
 */
const RETIRED_CARD_SUCCESSORS: readonly (readonly [
  [EffectCardGesture, ActiveCombatSpecialEffect],
  [EffectCardGesture, ActiveCombatSpecialEffect],
])[] = [
  [['attack', COMBAT_SPECIAL_EFFECT.Wound], ['dodge', COMBAT_SPECIAL_EFFECT.Wound]],
  [['attack', COMBAT_SPECIAL_EFFECT.Shield], ['dodge', COMBAT_SPECIAL_EFFECT.Shield]],
  [['magic', COMBAT_SPECIAL_EFFECT.BlockMagic], ['magic', COMBAT_SPECIAL_EFFECT.ForceAttack]],
  [['recover', COMBAT_SPECIAL_EFFECT.ForceRecover], ['recover', COMBAT_SPECIAL_EFFECT.DamageVsHurt]],
  // Execute Heal became Reaper: the same condition (foe below the execute line), paid as damage.
  [['recover', COMBAT_SPECIAL_EFFECT.HealVsHurt], ['recover', COMBAT_SPECIAL_EFFECT.DamageVsHurt]],
  // Force Magic had no payoff card, scored nothing and won zero contexts at every printing (Row 72).
  [['magic', COMBAT_SPECIAL_EFFECT.ForceMagic], ['magic', COMBAT_SPECIAL_EFFECT.Execute]],
  [['recover', COMBAT_SPECIAL_EFFECT.Lifesteal], ['recover', COMBAT_SPECIAL_EFFECT.HealVsLow]],
  // Wound Heal was itself replaced by Last Stand: the heal that read the FOE's wound only paid
  // against wound kits, and the one that reads your own bar pays in every fight.
  [['recover', COMBAT_SPECIAL_EFFECT.HealVsWounded], ['recover', COMBAT_SPECIAL_EFFECT.HealVsLow]],
];

const LEGACY_CARD_ID_ALIASES: Record<EffectCardId, EffectCardId> = Object.fromEntries(
  RETIRED_CARD_SUCCESSORS.flatMap(([[fromSuit, fromEffect], [toSuit, toEffect]]) =>
    CARD_TIER_ORDER.map((tier) => [
      cardIdFor(fromSuit, fromEffect, tier),
      cardIdFor(toSuit, toEffect, tier),
    ])
  )
);

/**
 * The id a stored card id means TODAY — itself, or its successor when the card was retired.
 * The one place a collection row, a loadout column or a purchase meets the retirement table.
 */
export function currentCardId(id: EffectCardId): EffectCardId {
  return LEGACY_CARD_ID_ALIASES[id] ?? id;
}

/**
 * Card lookup by id — O(1), and the only thing that turns a stored id back into a card.
 *
 * Spans the whole catalogue, both printings: a battle saved at any point in the deck's history
 * still holds ids in its hand, and it has to keep resolving them or the hand turns into blanks
 * mid-round.
 */
/**
 * **THE FOUR BASE CARDS — what a slot with no collected skill plays.**
 *
 * A skill goes in your deck once you own a copy of it (`ownsSkill`). A pet that has collected
 * nothing therefore has EMPTY slots, and an empty slot still has to deal a card: the pile is
 * four slots times {@link EFFECT_CARD_COPIES}, and a slot that dealt nothing would hand that
 * pet a shorter hand than its opponent.
 *
 * So an empty slot deals THIS — a card carrying no special effect
 * (`COMBAT_SPECIAL_EFFECT.None`) and one small flat number: {@link BASE_CARD_CHIP} per copy, up
 * the same flat ladder as every other card in the deck, **paid in its own suit's currency**
 * ({@link BASE_CARD_UNIT_BY_GESTURE}) — damage on attack, magic and dodge, HP on recover.
 *
 * **IT USED TO BE WORTH NOTHING AT ALL, and that was the bug this number fixes.** A base card
 * with no value resolved to no outcome, which meant a new player's whole hand — five tiles, a
 * pitch step, four priced move buttons — decided exactly nothing, and the card layer only
 * started existing once they had collected something. It hits now. Not hard: three per copy is
 * a chip, and a full stack of four lands a point above what the deck's most expensive Common
 * lands with ONE copy. The empty slot is a floor, never a plan.
 *
 * Three properties, all load-bearing:
 *
 * 1. **They are NOT in `EFFECT_CARD_CATALOGUE`.** The catalogue is the sixteen SKILLS — what
 *    the shelf lists, the collection counts, the Store sells and the copy-odds table is about.
 *    A base card is none of those things, and putting it in the catalogue would put it in all
 *    of them. It is resolvable by id and nowhere else.
 * 2. **They print their number like everything else.** `getCardPreview` gives them the ordinary
 *    `value` kind, because the face's one rule is that it states what the fight rolls — and the
 *    fight now rolls three.
 * 3. **They are owned by every pet, always** (`isBaseCard` → `ownsSkill`). They are not a reward
 *    and cannot be collected; they are what is left when nothing is equipped.
 *
 * They still carry NO ELEMENT, so matched-element never touches them: a slot you have collected
 * nothing for is not the place to pay out the rider that rewards building a coloured deck.
 *
 * Their ids follow the same `{gesture}-{effect}` scheme as everything else — `attack-none` —
 * so a stored loadout or a stored `battle_state` holding one resolves through the same lookup.
 */
/**
 * **WHAT A BLANK PAYS IN — one magnitude ({@link BASE_CARD_CHIP}), four suits, two currencies.**
 *
 * The three damage suits pay damage and recover pays HP, and this table is the only place that
 * says so: the base card's `unit` is read off it, and the engine pays whatever the face prints
 * ({@link resolveMeld}'s `None` case). Nothing downstream carries a second copy of the rule.
 *
 * **It used to be `dmg` on all four, and a player found what that meant** — they committed
 * Recover with nothing bleeding and watched the opponent's bar drop. Recover is the one move in
 * the game that promises not to touch them; a slot they had not filled yet must not be what
 * breaks that promise. The blank still lands (it is a chip on the board either way), it just
 * lands in the currency its own suit deals in.
 */
export const BASE_CARD_UNIT_BY_GESTURE: Readonly<Record<EffectCardGesture, 'dmg' | 'HP'>> = {
  attack: 'dmg',
  magic: 'dmg',
  dodge: 'dmg',
  recover: 'HP',
};

const BASE_CARD_COPY: Record<EffectCardGesture, { name: string; headline: string; description: string }> = {
  attack: {
    name: 'Attack',
    headline: 'Just the move, and a little extra on it.',
    description: `An empty attack slot. Your Suikamon still attacks — this card adds ${BASE_CARD_CHIP} damage on top, and more for every copy you commit. Collect an attack skill and equip it here to trade this chip for something that does real work.`,
  },
  magic: {
    name: 'Magic',
    headline: 'Just the move, and a little extra on it.',
    description: `An empty magic slot. Your species signature still fires — this card adds ${BASE_CARD_CHIP} damage on top, and more for every copy you commit. Collect a magic skill and equip it here to trade this chip for something that does real work.`,
  },
  dodge: {
    name: 'Dodge',
    headline: 'Just the move, and a little extra on it.',
    description: `An empty dodge slot. Your Suikamon still dodges — this card adds ${BASE_CARD_CHIP} damage on top, and more for every copy you commit. Collect a dodge skill and equip it here to trade this chip for something that does real work.`,
  },
  recover: {
    name: 'Recover',
    headline: 'Just the move, and a little extra on it.',
    description: `An empty recover slot. Your Suikamon still recovers — this card adds ${BASE_CARD_CHIP} HP on top, and more for every copy you commit. Collect a recover skill and equip it here to trade this chip for something that does real work.`,
  },
};

/** One base card per suit, keyed by the suit it fills. */
export const BASE_CARD_BY_GESTURE: Readonly<Record<EffectCardGesture, EffectCard>> =
  Object.fromEntries(
    EFFECT_DECK_GESTURES.map((gesture) => [
      gesture,
      {
        id: cardIdFor(gesture, COMBAT_SPECIAL_EFFECT.None, 'rare'),
        effect: COMBAT_SPECIAL_EFFECT.None,
        gesture,
        // The bottom rung, because a base card is the least a slot can be — but it is not ON
        // the ladder: it has no copies to collect, so no surface ever prints a rung for it.
        tier: 'common' as CardTier,
        /*
         * **THE GESTURE'S OWN PAINTED ICON — the one that already exists.**
         *
         * It used to point at `/cards/{gesture}-base.png`, which was never drawn, so every
         * empty slot in every hand fell back to `EffectCardFace`'s stand-in: a system emoji,
         * rendered by the player's OS font rather than by the game, at whatever size and
         * baseline that font happens to use. A new player's hand is FIVE base cards, so the
         * screen the game opens on was the one screen with no game art on it at all.
         *
         * `COMBAT_ACTION_ART` is the same four illustrations the arena's move buttons use, in
         * the house style, and a base card is exactly "the move with nothing on it" — so the
         * move's own icon is not a stand-in for its art, it IS its art.
         */
        art: getCombatActionArt(gesture),
        /*
         * **THE ONE NUMBER AN EMPTY SLOT PRINTS.** Flat, elementless, and the same size on all
         * four suits — a base card is the absence of a choice, so it must not become one by
         * paying a different RATE depending on which slot happens to be empty. What does change
         * with the suit is the CURRENCY it is paid in, which is the opposite of a choice: the
         * blank does what its move does, three of it ({@link BASE_CARD_UNIT_BY_GESTURE}).
         */
        value: BASE_CARD_CHIP,
        unit: BASE_CARD_UNIT_BY_GESTURE[gesture],
        ...BASE_CARD_COPY[gesture],
      },
    ])
  ) as Record<EffectCardGesture, EffectCard>;

/**
 * **THE SUIKAMON CARD — a copy of the fighter itself, dealt from its own pile.**
 *
 * The one card in the deck that is not a move. It plays into no meld, adds no damage and
 * answers no legality question; what it does is take one of the five slots in a hand and sit
 * there. Hold {@link EVOLUTION_CARDS_PER_STEP} of them into an exchange and the Suikamon
 * **evolves on the spot** — bigger HP, bigger damage, and part of the HP gain restored as
 * a heal (`shared/domain/battleEvolution.ts`) — and the three are spent doing it.
 *
 * **That is the decision the card exists to create**, and it lives entirely in the pitch step:
 * a hand one card weaker this round, or three rounds from now a Suikamon that hits harder for
 * the rest of the fight. Pitch it and it goes to the discard like anything else.
 *
 * It follows the base cards' pattern exactly, for the same three reasons:
 *
 * 1. **NOT in `EFFECT_CARD_CATALOGUE`.** The catalogue is the sixteen collectible SKILLS —
 *    what the shelf lists, the Store sells and the copy ladder is about. A Suikamon card is
 *    none of those: it cannot be bought, equipped, upgraded or left out of a loadout.
 * 2. **Resolvable by id and nowhere else**, so a hand or a stored `battle_state` holding one
 *    reads back as a real card rather than as a blank.
 * 3. **Suitless** — its gesture is {@link SUIKAMON_CARD_SLOT}, which matches none of the four,
 *    so every "cards of my suit" question in the engine excludes it without being told to.
 *
 * Its art is the CARD BACK, and deliberately: the face a player sees is their own Suikamon,
 * substituted at render time from the fighter the hand belongs to (`EffectCardFace`), because
 * the catalogue is shared by every species and this card is a copy of whichever one is holding
 * it.
 */
export const SUIKAMON_CARD: EffectCard = {
  id: 'suikamon-card',
  effect: COMBAT_SPECIAL_EFFECT.None,
  gesture: SUIKAMON_CARD_SLOT,
  // Common for the same reason a base card is: it has no copies to collect, so it stands on no
  // rung of the tier ladder and no surface ever prints one for it.
  tier: 'common' as CardTier,
  art: EFFECT_CARD_BACK_ART,
  /*
   * **NAMED FOR THE RAIL IT ALREADY PRINTS.** `EffectCardFace` drops a card's name row when
   * the name only repeats the suit rail above it — the rule that stops a hand of base cards
   * printing "Attack" twice on every card. Calling this card exactly what its rail says puts
   * it under that same rule, so in a hand it is a picture of the Suikamon and nothing else,
   * and the words live on the detail view where there is room for them.
   */
  name: 'Suikamon',
  headline: `Hold ${EVOLUTION_CARDS_PER_STEP} and your Suikamon evolves.`,
  description:
    `A copy of your own Suikamon. It plays no move and deals no damage — it takes a slot in ` +
    `your hand and waits. Keep ${EVOLUTION_CARDS_PER_STEP} of them through the pitch and your ` +
    `Suikamon evolves the moment the round resolves: more HP, more damage, and the HP it gains ` +
    `is healed back on the spot. Pitch it and it goes to the discard like any other card.`,
};

/**
 * **THE SUIKAMON CARD'S RAIL** — the same shape as a suit's, for a card that has no suit.
 *
 * The card face prints a rail above the art naming what the card plays in. The Suikamon card
 * plays in nothing, so it cannot borrow one of the four — a rail reading "Attack" would say it
 * commits with attacks, which is the one thing it never does. It gets its own, and every field
 * says the same thing in the length that surface needs.
 */
export const SUIKAMON_SUIT_ROLE = {
  label: 'Suikamon',
  emoji: '🎴',
  role: 'Evolution',
  rule: `Hold ${EVOLUTION_CARDS_PER_STEP} at once and your Suikamon evolves — no move, no damage.`,
  playerThinks: 'A slot now, a bigger Suikamon later.',
  blurb: 'Plays no move. Costs a slot. Buys a rung.',
} as const;

/**
 * The rail for ANY card in a hand — the suit's for the four, the Suikamon card's for the one
 * that has none.
 *
 * Every surface that draws a card reads its rail through this rather than indexing
 * {@link EFFECT_SUIT_ROLE} directly, so a Suikamon card that reaches a screen nobody expected
 * it on renders as itself instead of as an unchecked lookup into the four suits.
 */
export function getSuitRole(slot: EffectCardSlot): typeof SUIKAMON_SUIT_ROLE | (typeof EFFECT_SUIT_ROLE)[EffectCardGesture] {
  return isGestureSlot(slot) ? EFFECT_SUIT_ROLE[slot] : SUIKAMON_SUIT_ROLE;
}

/** Is this the Suikamon card rather than a move? The one test anything should make. */
export function isSuikamonCard(id: EffectCardId): boolean {
  return id === SUIKAMON_CARD.id;
}

/** The four base cards, suit order. */
export const BASE_CARDS: readonly EffectCard[] = EFFECT_DECK_GESTURES.map(
  (gesture) => BASE_CARD_BY_GESTURE[gesture]
);

const BASE_CARD_IDS = new Set(BASE_CARDS.map((card) => card.id));

/**
 * Is this the empty-slot card rather than a skill?
 *
 * The one test anything should make. Comparing against `DEFAULT_CARD_LOADOUT` instead works
 * today and stops working the moment a slot's default changes; comparing `effect === none`
 * works too but reads as a fact about the engine rather than about the deck.
 */
export function isBaseCard(id: EffectCardId): boolean {
  return BASE_CARD_IDS.has(id);
}

/**
 * Card lookup covers the sixteen skills in every printing AND the four base cards — a stored
 * battle or a stored loadout may hold either, and an id that stops resolving turns a hand into
 * blanks mid-round.
 */
const EFFECT_DECK_BY_ID = new Map(
  [...EFFECT_CARD_CATALOGUE, ...BASE_CARDS, SUIKAMON_CARD].map((card) => [card.id, card])
);

/**
 * Where each card's number came from, so the page can write its honesty note from data
 * rather than a hand-kept sentence that drifts the moment a species changes.
 *
 * Keyed by the RARE id: a Common's number is its Rare's, scaled, so the two always share a
 * provenance and recording it twice would be recording the same fact twice.
 */
export const CARD_VALUE_SOURCE: Record<string, CardValueSource> = Object.fromEntries(
  CARD_DEFINITIONS.map((definition) => {
    const id = cardIdFor(definition.gesture, definition.effect, 'rare');
    if (BINARY_CARD_EFFECTS.includes(definition.effect)) return [id, 'binary' as CardValueSource];
    const derived = resolveCardValue(definition);
    return [id, (derived === undefined ? 'authored' : 'derived') as CardValueSource];
  })
);

/** Cards whose number is real live roster data, by name — for the page's note. */
export function getCardsByValueSource(source: CardValueSource): EffectCard[] {
  return EFFECT_CARD_CATALOGUE.filter(
    (card) => card.tier === 'rare' && CARD_VALUE_SOURCE[card.id] === source
  );
}

/**
 * **HOW MANY SUITS A COLOUR PRINTS ON — the ACHIEVED number, not the floor it was aimed at.**
 *
 * {@link MIN_ELEMENT_COLOUR_SLOTS} is what the coverage pass tries to reach; this is what it
 * reached. The two are different questions and the page must print the second, because the pass
 * can be blocked — every possible donor already at its own floor — and a screen that stated the
 * intention would then be telling a Keeper about a build the deck cannot give them.
 *
 * Counts SUITS rather than cards, which is the unit the player builds in: two cards of your
 * colour on one suit is one slot you can spend, not two.
 */
export function getColourSuitCount(element: ElementId): number {
  return new Set(
    EFFECT_CARD_CATALOGUE.filter((card) => card.element === element).map((card) => card.gesture)
  ).size;
}

/** The thinnest colour the ROSTER actually fields — what `/battle-cards` promises a Keeper. */
export function getMinColourSuitCount(): number {
  const rosterElements = [
    ...new Set(
      SUIKAMON_SPECIES.filter((species) => species.combat !== undefined).map(
        (species) => species.element
      )
    ),
  ];
  return Math.min(...rosterElements.map(getColourSuitCount));
}

/** The catalogue filtered to one suit — every card that can sit in that slot, Rares first. */
export function getEffectCardsByGesture(gesture: EffectCardGesture): EffectCard[] {
  return EFFECT_CARD_CATALOGUE.filter((card) => card.gesture === gesture);
}

/** One card by id. The single place a stored `battle_state` id becomes a card again. */
export function getEffectCardById(id: EffectCardId): EffectCard | undefined {
  return EFFECT_DECK_BY_ID.get(LEGACY_CARD_ID_ALIASES[id] ?? id);
}

